import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getApi } from "../../../apis/api";
import { GET_HELPDESK_STATIC_DATA } from "../../../apis/end-points";
import { GENERAL_ERROR_MESSAGE } from "../../../utils/message";
import type { RootState } from "../../store";
import { HelpdeskStaticDataDto, HelpdeskStaticDataState } from "./helpdeskStaticDataTypes";

/**
 * Helpdesk static (lookup) data — time zones, priority icons — from
 * GET /service/helpdesk-group/staticdata.
 *
 * Loaded on demand by the first Helpdesk screen that needs it and then cached
 * in memory for the session (not persisted, so a reload picks up changes).
 * Screens read it through HelpdeskShared/useHelpdeskStaticData.
 *
 * Only admin / Super Admin may load it — the System Configuration screens that
 * use it (Business Hours, Ticket Fields & Statuses) are theirs. Communications
 * and the order's Conversations tab never ask for it (decision D41).
 */

/** The roles allowed to load the Helpdesk static data. */
const HELPDESK_STATIC_DATA_ROLES = ["admin", "customersuperadmin"];

const initialState: HelpdeskStaticDataState = {
  timeZones: [],
  iconOptions: [],
  status: "idle",
  error: null,
};

/**
 * Fetches the static data once. `condition` skips the request while it is
 * loading or already loaded, so any number of screens can ask for it and only
 * one request goes out; after a failure the next ask retries. It also skips it
 * for any role outside HELPDESK_STATIC_DATA_ROLES.
 */
export const fetchHelpdeskStaticData = createAsyncThunk<
  HelpdeskStaticDataDto,
  void,
  { state: RootState; rejectValue: string }
>(
  "helpdeskStaticData/fetch",
  async (_, { rejectWithValue }) => {
    const response = await getApi<HelpdeskStaticDataDto>(GET_HELPDESK_STATIC_DATA);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error?.message ?? GENERAL_ERROR_MESSAGE);
    }
    return response.data;
  },
  {
    condition: (_, { getState }) => {
      const { auth, helpdeskStaticData } = getState();
      if (!HELPDESK_STATIC_DATA_ROLES.includes(auth.userRole ?? "")) return false;
      const { status } = helpdeskStaticData;
      return status === "idle" || status === "failed";
    },
  }
);

const helpdeskStaticDataSlice = createSlice({
  name: "helpdeskStaticData",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHelpdeskStaticData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchHelpdeskStaticData.fulfilled, (state, action) => {
        state.status = "succeeded";
        // As the API sent them; a missing list stays empty.
        state.timeZones = action.payload.timeZone ?? [];
        state.iconOptions = action.payload.iconOptions ?? [];
      })
      .addCase(fetchHelpdeskStaticData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? action.error.message ?? GENERAL_ERROR_MESSAGE;
      });
  },
});

export const selectHelpdeskStaticData = (state: RootState) => state.helpdeskStaticData;

/** The cached priority icon options — read-only, never triggers a fetch. */
export const selectHelpdeskIconOptions = (state: RootState) => state.helpdeskStaticData.iconOptions;

export default helpdeskStaticDataSlice.reducer;
